import { api } from '../../shared/api/client'
import type {
  DesignerTheme,
  DesignerLayout,
  DesignType,
  DesignerStats,
  Balance,
  Payout,
  TariffRow,
  BaseTariffRow,
  DesignerBalance,
} from './types'
import type { SeoDictionaryField, SeoDictionaryItem } from '../pipeline-rules/api'

/** Гейтвей отдаёт { success, data } — разворачиваем до data. */
const unwrap = (r: any) => r.data?.data ?? r.data

export interface CreateThemePayload {
  name: string
  category?: string
  theme?: string
  brand_ru?: string
  brand_en?: string
  gender?: string
  age?: string
  profession?: string
  occasion?: string
  season?: string
  seasonal?: number
  holiday_date?: string | null
  recipient_statuses?: string[]
  personas?: string[]
  deadline?: string | null
  designer_id?: number | null
  source?: string
}

export interface CreateLayoutPayload {
  theme_number: number
  type: string
  image_text?: string
  seasonal?: number
  [key: string]: any
}

export interface SubmitResult {
  design_code: string
  passed: boolean
  pipeline_status: string
  tech_check: {
    passed: boolean
    min_dpi: number
    required: { psd: boolean; pdf: boolean; jpg: boolean }
    jpg: { name: string; dpi_x: number | null; dpi_y: number | null } | null
    issues: string[]
    checked_at: string
  }
}

export const designerAPI = {
  getThemes: (params?: { search?: string; status?: string }): Promise<{ themes: DesignerTheme[]; total: number }> =>
    api.get('/designer/themes', { params }).then(unwrap),

  getTheme: (themeNumber: number): Promise<{ theme: DesignerTheme; layouts: DesignerLayout[] }> =>
    api.get(`/designer/themes/${themeNumber}`).then(unwrap),

  createTheme: (payload: CreateThemePayload): Promise<DesignerTheme> =>
    api.post('/designer/themes', payload).then(unwrap),

  updateTheme: (themeNumber: number, payload: Partial<CreateThemePayload> & { status?: string }): Promise<DesignerTheme> =>
    api.put(`/designer/themes/${themeNumber}`, payload).then(unwrap),

  claimTheme: (themeNumber: number): Promise<DesignerTheme> =>
    api.post(`/designer/themes/${themeNumber}/claim`).then(unwrap),

  createLayout: (payload: CreateLayoutPayload): Promise<DesignerLayout> =>
    api.post('/designer/layouts', payload).then(unwrap),

  submitLayout: (
    designCode: string,
    files: { name: string; mime: string; base64: string }[],
  ): Promise<SubmitResult> =>
    api.post(`/designer/layouts/${designCode}/submit`, { files }).then(unwrap),

  getTypes: (category?: string): Promise<{ types: DesignType[] }> =>
    api.get('/designer/types', { params: category ? { category } : undefined }).then(unwrap),

  /** Образцы тематики (картинки-примеры от продакта) → массив data URL. */
  getSamples: (themeNumber: number): Promise<{ images: string[]; image: string | null }> =>
    api.get(`/designer/themes/${themeNumber}/sample`).then(unwrap),

  /** Справочник атрибутов через self-contained API ЛК. */
  getDictionaries: (field?: SeoDictionaryField): Promise<SeoDictionaryItem[]> =>
    api.get('/designer/dictionaries', { params: field ? { field } : undefined }).then((response) => {
      const data = unwrap(response)
      return Array.isArray(data) ? data : []
    }),

  /** Серверный рендер PDF → JPEG через право DesignerCabinet:UPDATE. */
  renderPdfThumb: (pdfBase64: string, width = 400) =>
    api.post('/designer/pdf-thumb', { pdf_base64: pdfBase64, width }, { timeout: 60000 }),

  /** Ссылка на папку с исходниками (одна на тематику). */
  setSource: (themeNumber: number, sourceFolderUrl: string): Promise<{ ok: boolean }> =>
    api.post(`/designer/themes/${themeNumber}/source`, { source_folder_url: sourceFolderUrl }).then(unwrap),

  /** PDF-поток: пачка PDF-макетов (по каждому — тип, превью, опц. design_code для замены). */
  uploadPdfs: (payload: {
    theme_number: number
    source_folder_url: string
    files: { type: string; name: string; base64: string; preview?: string; design_code?: string }[]
  }): Promise<{ created: number; layouts: { design_code: string; type: string }[] }> =>
    api.post('/designer/pdf-layouts', payload).then(unwrap),

  /** Сменить тип загруженного макета. */
  updateLayoutType: (designCode: string, type: string): Promise<{ ok: boolean }> =>
    api.post(`/designer/layouts/${designCode}/type`, { type }).then(unwrap),

  /** Удалить загруженный макет. */
  deleteLayout: (designCode: string): Promise<{ ok: boolean }> =>
    api.post(`/designer/layouts/${designCode}/delete`).then(unwrap),

  /** Исходный PDF макета (для клиентского рендера превью — бэкфилл). */
  getLayoutPdf: (designCode: string, type?: string): Promise<Blob> => {
    const url = type
      ? `/designer/layouts/${designCode}/media/${encodeURIComponent(type)}/${designCode}.pdf`
      : `/designer/layouts/${designCode}/media/${designCode}.pdf`
    return api.get(url, { responseType: 'blob' }).then((r) => r.data as Blob)
  },

  /** Сохранить сгенерированное превью к существующему макету (бэкфилл). */
  setLayoutPreview: (designCode: string, preview: string): Promise<{ ok: boolean; preview_url: string }> =>
    api.post(`/designer/layouts/${designCode}/preview`, { preview }).then(unwrap),

  // ── Деньги ──
  getStats: (designerId?: number): Promise<DesignerStats> =>
    api.get('/designer/stats', { params: designerId ? { designer_id: designerId } : undefined }).then(unwrap),

  getPayouts: (designerId?: number): Promise<{ balance: Balance; payouts: Payout[] }> =>
    api.get('/designer/payouts', { params: designerId ? { designer_id: designerId } : undefined }).then(unwrap),

  getTariffs: (designerId?: number): Promise<{ tariffs: TariffRow[] }> =>
    api.get('/designer/tariffs', { params: designerId ? { designer_id: designerId } : undefined }).then(unwrap),

  getBaseTariffs: (): Promise<{ tariffs: BaseTariffRow[] }> =>
    api.get('/designer/base-tariffs').then(unwrap),

  upsertTariff: (payload: { design_type: string; price: number; designer_id?: number }): Promise<{ ok: boolean }> =>
    api.post('/designer/tariffs', payload).then(unwrap),

  getBalances: (): Promise<{ designers: DesignerBalance[] }> =>
    api.get('/designer/balances').then(unwrap),

  createPayout: (payload: { designer_id: number; amount: number; note?: string }): Promise<{ ok: boolean; balance: Balance }> =>
    api.post('/designer/payouts', payload).then(unwrap),
}
